import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { inventoryService } from '../../../services/inventoryService';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import { FiChevronDown, FiChevronRight, FiPackage, FiBox } from 'react-icons/fi';
import InventoryAssetsTab from '../../../components/InventoryAssetsTab';
import ActivityTimeline from '../../../components/ActivityTimeline';
import { adminAPI } from '../../../services/api';

const formatInventoryLinkageDetails = (details) => {
  if (!details || typeof details !== 'object') {
    return '';
  }

  const labels = {
    inventoryStockRows: 'stock rows',
    inventoryAssetCount: 'inventory assets',
    requestLineItemCount: 'request line items',
    requestLineItemAssetCount: 'request line item assets',
    inboundProjectLineCount: 'manifest lines',
    inboundProjectReceiptLineCount: 'manifest receipt lines'
  };

  const activeLinks = Object.entries(details)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${count} ${labels[key] || key}`);

  return activeLinks.length > 0 ? ` Linked records: ${activeLinks.join(', ')}.` : '';
};

// Component for displaying pallet and carton breakdown
const PalletCartonBreakdown = ({ data }) => {
  const [expandedPallets, setExpandedPallets] = useState(new Set());

  const togglePallet = (palletId) => {
    const newExpanded = new Set(expandedPallets);
    if (newExpanded.has(palletId)) {
      newExpanded.delete(palletId);
    } else {
      newExpanded.add(palletId);
    }
    setExpandedPallets(newExpanded);
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions) return '';
    
    // Handle if dimensions is a string (JSON text from database)
    if (typeof dimensions === 'string') {
      try {
        const parsed = JSON.parse(dimensions);
        if (!parsed || (!parsed.length && !parsed.width && !parsed.height)) return '';
        return `${parsed.length || 0}"×${parsed.width || 0}"×${parsed.height || 0}"`;
      } catch (e) {
        return dimensions; // Return as-is if not valid JSON
      }
    }
    
    // Handle if dimensions is already an object
    if (typeof dimensions === 'object') {
      if (!dimensions.length && !dimensions.width && !dimensions.height) return '';
      return `${dimensions.length || 0}"×${dimensions.width || 0}"×${dimensions.height || 0}"`;
    }
    
    return '';
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'intact': 'bg-green-100 text-green-800',
      'partially_depalletized': 'bg-yellow-100 text-yellow-800',
      'fully_depalletized': 'bg-orange-100 text-orange-800',
      'empty': 'bg-gray-100 text-gray-800'
    };
    
    const statusLabels = {
      'intact': 'Intact',
      'partially_depalletized': 'Partially Depalletized',
      'fully_depalletized': 'Fully Depalletized',
      'empty': 'Empty'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  if (!data.pallets?.length && !data.individualItems?.count) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h6 className="text-sm font-medium text-gray-900 mb-4">Item Details</h6>
      
      {/* Pallets Section */}
      {data.pallets?.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h7 className="text-sm font-medium text-gray-700 flex items-center">
              <FiPackage className="mr-2" />
              Pallets ({data.pallets.length})
            </h7>
          </div>
          
          {data.pallets.map((pallet) => (
            <div key={pallet.palletId} className="border border-gray-200 rounded-lg">
              <div 
                className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => togglePallet(pallet.palletId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {expandedPallets.has(pallet.palletId) ? (
                      <FiChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <FiChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">{pallet.palletLabel}</span>
                    <span className="text-gray-500">|</span>
                    <span className="text-sm text-gray-600">{pallet.binLocation || 'No bin assigned'}</span>
                    <span className="text-gray-500">|</span>
                    {getStatusBadge(pallet.status)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDimensions(pallet.dimensions)} | {pallet.weight ? `${pallet.weight} lbs` : 'No weight'}
                  </div>
                </div>
              </div>
              
              {expandedPallets.has(pallet.palletId) && (
                <div className="px-4 py-3 border-t border-gray-200 bg-white">
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Asset Count:</span>
                        <span className="ml-2 text-gray-600">{pallet.assetCount}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Remaining Quantity:</span>
                        <span className="ml-2 text-gray-600">{pallet.remainingQuantity || 'N/A'}</span>
                      </div>
                    </div>
                    
                    {pallet.lineItems?.length > 0 && (
                      <div className="mt-3">
                        <h8 className="text-xs font-medium text-gray-700 mb-2">Line items on this pallet:</h8>
                        <div className="space-y-1">
                          {pallet.lineItems.map((lineItem, index) => (
                            <div key={index} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                              <span>{lineItem.productSku}</span>
                              <span>
                                {lineItem.quantityReceived != null
                                  ? `${lineItem.quantityReceived} / ${lineItem.quantity} received`
                                  : `${lineItem.quantity} units`}
                              </span>
                              <span>{formatDimensions(lineItem.dimensions)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}



      {/* Individual Items Section */}
      {data.individualItems?.count > 0 && (
        <div className="space-y-3">
          <h7 className="text-sm font-medium text-gray-700 flex items-center">
            <FiBox className="mr-2" />
            Individual Items
          </h7>
          
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <span className="font-medium text-gray-900">Loose Items</span>
              <span className="text-gray-500">|</span>
              <span className="text-sm text-gray-600">
                {data.individualItems.binLocations.length > 0 
                  ? data.individualItems.binLocations.join(', ')
                  : 'Various locations'
                }
              </span>
              <span className="text-gray-500">|</span>
              <span className="text-sm text-gray-600">{data.individualItems.count} units</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InventoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [requestFilter, setRequestFilter] = useState('pending');
  const [actionError, setActionError] = useState('');
  
  // Get current user info to determine if internal user
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const isInternalUser = userInfo.user_type === 'internal';
  
  // Fetch inventory item details
  const { data: item, isLoading, error } = useQuery(
    ['inventoryItem', id],
    () => inventoryService.getInventoryItem(id),
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
    }
  );

  const deleteMutation = useMutation(
    () => inventoryService.deleteInventoryItem(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        navigate('/admin/inventory');
      },
      onError: (error) => {
        const message = error.response?.data?.message || error.message || 'Failed to delete inventory item';
        const details = formatInventoryLinkageDetails(error.response?.data?.details);
        setActionError(`${message}${details}`);
      }
    }
  );
  
  // Filter requests based on selected filter
  const filteredRequests = item?.currentRequests?.filter(request => {
    if (requestFilter === 'pending') {
      return ['pending', 'approved', 'in_progress', 'pending_receive'].includes(request.status);
    } else if (requestFilter === 'completed') {
      return ['completed', 'cancelled'].includes(request.status);
    }
    return true; // 'all'
  }) || [];
  
  // Helper function to format request number
  const formatRequestNumber = (requestNumber) => {
    if (!requestNumber) return '';
    // Split by dash and take first two parts: REQ-20241225-1234 -> REQ-20241225
    const parts = requestNumber.split('-');
    return parts.length >= 2 ? `${parts[0]}-${parts[1]}` : requestNumber;
  };
  
  // Helper function to format status display
  const formatStatus = (status, requestType) => {
    const isOutbound = ['ship', 'pick_pack', 'kit_build'].includes(requestType);
    const statusMap = {
      'pending': 'Pending',
      'approved': isOutbound ? 'Pending Shipment' : 'Approved',
      'in_progress': 'In Progress',
      'pending_receive': 'Scheduled',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const handleDelete = async () => {
    setActionError('');

    const confirmed = window.confirm(
      'Delete this inventory item? This only works if the item has no linked inventory, request, or manifest records.'
    );

    if (!confirmed) {
      return;
    }

    await deleteMutation.mutateAsync();
  };
  
  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={`Error loading inventory item details: ${error.message}`} />;
  if (!item) return <ErrorAlert message="Inventory item not found." />;

  const detailLabelClass = 'text-sm font-medium text-gray-500';
  const detailValueClass = 'mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2';
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {actionError && (
          <div className="mb-6">
            <ErrorAlert message={actionError} />
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {item.productName || 'Inventory Item'} ({item.productSku})
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Inventory item details and status information
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete Item'}
              </button>
              <Link
                to={`/admin/inventory/${id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Edit Item
              </Link>
              <button
                onClick={() => navigate('/admin/inventory')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Inventory
              </button>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="border-t border-gray-200">
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">Select a tab</label>
              <select
                id="tabs"
                name="tabs"
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="overview">Overview</option>
                <option value="assets">Assets &amp; Projects</option>
                <option value="warehouses">Stock by Warehouse</option>
                <option value="requests">Current Requests</option>
                <option value="timeline">Timeline</option>
              </select>
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`${
                      activeTab === 'overview'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('assets')}
                    className={`${
                      activeTab === 'assets'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Assets & Projects
                  </button>
                  <button
                    onClick={() => setActiveTab('warehouses')}
                    className={`${
                      activeTab === 'warehouses'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Stock by Warehouse
                  </button>
                  <button
                    onClick={() => setActiveTab('requests')}
                    className={`${
                      activeTab === 'requests'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Current Requests ({filteredRequests.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('timeline')}
                    className={`${
                      activeTab === 'timeline'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Timeline
                  </button>
                </nav>
              </div>
            </div>
          </div>
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (<></>)}
          
          {/* Stock by Warehouse Tab */}
          {/* Assets Tab */}
          {activeTab === 'assets' && item && (
            <InventoryAssetsTab inventoryItemId={item.id} />
          )}

          {activeTab === 'warehouses' && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Stock by Warehouse</h4>
              {isInternalUser && (
                <p className="text-sm text-gray-600 mb-6">
                  Showing stock levels across all warehouses that {item.Customer?.companyName || 'this customer'} has access to.
                </p>
              )}
              
              {item.warehouseStockBreakdown && item.warehouseStockBreakdown.length > 0 ? (
                <div className="space-y-6">
                  {item.warehouseStockBreakdown.map((warehouse) => {
                    // Find corresponding pallet/carton breakdown for this warehouse
                    const palletCartonData = item.palletCartonBreakdown?.find(
                      breakdown => breakdown.warehouseId === warehouse.warehouseId
                    );
                    
                    return (
                      <div key={warehouse.warehouseId} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                          <h5 className="text-lg font-medium text-gray-900">
                            {warehouse.warehouseName}
                            {warehouse.binLocations && (
                              <span className="text-sm text-gray-500 font-normal ml-2">
                                - {warehouse.binLocations}
                              </span>
                            )}
                          </h5>
                        </div>
                        <div className="p-6">
                          {/* Stock Status Cards */}
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
                            <div className="bg-green-50 overflow-hidden shadow rounded-lg">
                              <div className="p-5">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-5 w-0 flex-1">
                                    <dl>
                                      <dt className="text-sm font-medium text-gray-500 truncate">In Stock</dt>
                                      <dd className="text-lg font-medium text-gray-900">{warehouse.inStock}</dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-blue-50 overflow-hidden shadow rounded-lg">
                              <div className="p-5">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-5 w-0 flex-1">
                                    <dl>
                                      <dt className="text-sm font-medium text-gray-500 truncate">Pending Receive</dt>
                                      <dd className="text-lg font-medium text-gray-900">{warehouse.pendingReceive}</dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-yellow-50 overflow-hidden shadow rounded-lg">
                              <div className="p-5">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                                      <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-5 w-0 flex-1">
                                    <dl>
                                      <dt className="text-sm font-medium text-gray-500 truncate">Pending Shipment</dt>
                                      <dd className="text-lg font-medium text-gray-900">{warehouse.pendingShipment}</dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-red-50 overflow-hidden shadow rounded-lg">
                              <div className="p-5">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-5 w-0 flex-1">
                                    <dl>
                                      <dt className="text-sm font-medium text-gray-500 truncate">In Hold</dt>
                                      <dd className="text-lg font-medium text-gray-900">{warehouse.inHold}</dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Enhanced Pallet and Carton Breakdown */}
                          {palletCartonData && (
                            <PalletCartonBreakdown data={palletCartonData} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-sm text-gray-500">
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No warehouse access</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {isInternalUser ? "This customer doesn't have access to any warehouses or no stock found." : "No stock found in accessible warehouses."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Current Requests Section */}
          {activeTab === 'requests' && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-900">Current Requests</h4>
                <div className="flex items-center space-x-4">
                  <label htmlFor="requestFilter" className="text-sm font-medium text-gray-700">Filter:</label>
                  <select
                    id="requestFilter"
                    value={requestFilter}
                    onChange={(e) => setRequestFilter(e.target.value)}
                    className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                    <option value="pending">Active Requests</option>
                    <option value="completed">Completed</option>
                    <option value="all">All Requests</option>
                  </select>
                </div>
              </div>
              
              {filteredRequests.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Request #
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        {isInternalUser && (
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Company
                          </th>
                        )}
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatRequestNumber(request.requestNumber)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {request.requestType === 'incoming' && 'Incoming'}
                            {request.requestType === 'ship' && 'Ship'}
                            {request.requestType === 'crossdock' && 'Cross-Dock'}
                            {request.requestType === 'inbound_manifest' && 'Inbound Manifest'}
                            {request.requestType === 'pick_pack' && 'Pick & Pack'}
                            {request.requestType === 'kit_build' && 'Kit Build'}
                          </td>
                          {isInternalUser && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {request.companyName}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {request.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {formatStatus(request.status, request.requestType)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link 
                              to={`/admin/requests/${request.id}`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-sm text-gray-500">
                    {requestFilter === 'pending' && 'No active requests found for this item.'}
                    {requestFilter === 'completed' && 'No completed requests found for this item.'}
                    {requestFilter === 'all' && 'No requests found for this item.'}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Item Details (Overview tab) */}
          {activeTab === 'overview' && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Product Details */}
                <div className="lg:col-span-2">
                  <dl className="divide-y divide-gray-200">
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Product Name</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.productName}</dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Product SKU</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.productSku}</dd>
                    </div>
                    {item.internalSku && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Internal SKU</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.internalSku}</dd>
                      </div>
                    )}
                    {item.alternatePartNumber && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Manufacturer SKU</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.alternatePartNumber}</dd>
                      </div>
                    )}
                    {isInternalUser && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Customer</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.Customer?.companyName || item.Customer?.name || 'Unknown'}</dd>
                      </div>
                    )}
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Packaging</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {item.packagingType === 'carton' ? 'Carton' : 'Individual Units'}
                        {item.unitsPerCarton && item.packagingType === 'carton' && (
                          <span className="text-gray-500 ml-1">({item.unitsPerCarton} units/carton)</span>
                        )}
                      </dd>
                    </div>
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className={detailLabelClass}>Measurements</dt>
                      <dd className={`${detailValueClass} space-y-3`}>
                        {item.packagingType === 'carton' && (
                          <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                            <h5 className="text-sm font-semibold text-gray-900">Carton Details</h5>
                            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Carton Weight</div>
                                <div className="mt-1 text-sm text-gray-900">
                                  {item.cartonWeight || '—'}{item.cartonWeight ? ' lbs' : ''}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Carton Dimensions</div>
                                <div className="mt-1 text-sm text-gray-900">
                                  {(item.cartonLength || item.cartonWidth || item.cartonHeight)
                                    ? `${item.cartonLength || 0}" x ${item.cartonWidth || 0}" x ${item.cartonHeight || 0}"`
                                    : '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="rounded-md border border-gray-200 bg-white p-4">
                          <h5 className="text-sm font-semibold text-gray-900">Item Details</h5>
                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Item Weight</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {item.weightLbs || item.individualDimensions?.weight || '—'}
                                {(item.weightLbs || item.individualDimensions?.weight) ? ' lbs' : ''}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Item Dimensions</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {(item.length || item.width || item.height)
                                  ? `${item.length || 0}" x ${item.width || 0}" x ${item.height || 0}"`
                                  : '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </dd>
                    </div>

                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Unit of Measurement</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {item.unitOfMeasurement || 'Each'}
                        {item.palletQuantity && (
                          <span className="text-gray-500 ml-1">({item.palletQuantity} units/pallet)</span>
                        )}
                      </dd>
                    </div>

                    {(item.serialNumber || item.macAddress) && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Asset Identification</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {item.serialNumber && <div>Serial: {item.serialNumber}</div>}
                          {item.macAddress && <div>MAC: {item.macAddress}</div>}
                        </dd>
                      </div>
                    )}

                    {item.description && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Product Description</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.description}</dd>
                      </div>
                    )}
                    {item.vendor && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.vendor}</dd>
                      </div>
                    )}
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Approximate Value ($)</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {item.value ? `$${item.value}` : '—'}
                      </dd>
                    </div>

                    {item.notes && (
                      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Notes</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.notes}</dd>
                      </div>
                    )}
                
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Right: Stock Summary Sidebar */}
                <div className="lg:col-span-1">
                  {item.statusBreakdown && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Operational Stock Snapshot</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Available quantity and low-stock alerts are based on <span className="font-medium">In Stock</span>. Pending and held units are shown separately.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                            <span className="text-sm text-gray-600">In Stock</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{item.statusBreakdown.inStock}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                            <span className="text-sm text-gray-600">Pending Receive</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{item.statusBreakdown.pendingReceive}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
                            <span className="text-sm text-gray-600">Pending Shipment</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{item.statusBreakdown.pendingShipment}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
                            <span className="text-sm text-gray-600">On Hold</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{item.statusBreakdown.inHold}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Total</span>
                            <span className="text-sm font-bold text-gray-900">
                              {(item.statusBreakdown.inStock || 0) + (item.statusBreakdown.pendingReceive || 0) + (item.statusBreakdown.pendingShipment || 0) + (item.statusBreakdown.inHold || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <ActivityTimeline
                  fetchFn={(params) => adminAPI.getEntityTimeline('InventoryItem', id, params)}
                  entityType="InventoryItem"
                  entityId={id}
                  limit={25}
                  title={null}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryDetail; 
